import React from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
} from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AvatarCircle } from '@/components';
import { IConversation, IConversationAccount } from '@/types/admin/messages';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';

interface ChatDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  /**
   * Fired by the native modal once it has actually been dismissed (iOS). A
   * host that wants to navigate away after closing the sheet must wait for
   * this; see ChatScreen.
   */
  onDismiss?: () => void;
  conversation: IConversation;
  onParticipantPress?: (participant: IConversationAccount) => void;
}

export const ChatDetailsModal: React.FC<ChatDetailsModalProps> = ({
  visible,
  onClose,
  onDismiss,
  conversation,
  onParticipantPress,
}) => {
  const { redesign } = useAdminColors();
  // Filter participants (residents) and admins
  const participants = conversation.conversationAccounts?.filter(
    (acct: IConversationAccount) => acct.type === 'resident'
  ) || [];

  const threadOwners = conversation.conversationAccounts?.filter(
    (acct: IConversationAccount) => acct.type === 'admin'
  ) || [];

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
      onDismiss={onDismiss}
    >
      <View style={[styles.container, { backgroundColor: redesign.bg.base }]}>
        {/* Header */}
        <View
          style={[
            styles.header,
            {
              backgroundColor: redesign.bg.base,
              borderBottomColor: redesign.card.border,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <SVG_COMPONENTS.CHEVRON_LEFT
              width={24}
              height={24}
              color={redesign.text.primary}
            />
          </TouchableOpacity>

          <Text style={[styles.headerTitle, { color: redesign.text.primary }]}>
            Conversation Details
          </Text>

          <View style={styles.headerSpacer} />
        </View>

        {/* Content */}
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Conversation Info */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: redesign.text.primary }]}>
                Conversation
              </Text>
            </View>
            <View style={styles.sectionBody}>
              <GlassCard padding={16}>
                <Text
                  style={[styles.conversationSubject, { color: redesign.text.primary }]}
                  numberOfLines={2}
                >
                  {conversation.subject}
                </Text>
                <Text style={[styles.conversationDate, { color: redesign.text.muted }]}>
                  Created {formatDate(conversation.createdAt)}
                </Text>
              </GlassCard>
            </View>
          </View>

          {/* Participants Section */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: redesign.text.primary }]}>
                Participants
              </Text>
              <Text style={[styles.sectionCount, { color: redesign.text.muted }]}>
                {participants.length}
              </Text>
            </View>

            {participants.length === 0 ? (
              <View style={styles.emptyState}>
                <SVG_COMPONENTS.GROUP
                  width={32}
                  height={32}
                  color={redesign.text.muted}
                />
                <Text style={[styles.emptyStateText, { color: redesign.text.muted }]}>
                  No participants
                </Text>
              </View>
            ) : (
              <View style={styles.sectionBody}>
                {participants.map((participant) => (
                  <ParticipantRow
                    key={participant.id}
                    participant={participant}
                    onPress={onParticipantPress}
                  />
                ))}
              </View>
            )}
          </View>

          {/* Thread Owners Section */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: redesign.text.primary }]}>
                Thread Owners
              </Text>
              <Text style={[styles.sectionCount, { color: redesign.text.muted }]}>
                {threadOwners.length}
              </Text>
            </View>

            {threadOwners.length === 0 ? (
              <View style={styles.emptyState}>
                <SVG_COMPONENTS.ADMIN
                  width={32}
                  height={32}
                  color={redesign.text.muted}
                />
                <Text style={[styles.emptyStateText, { color: redesign.text.muted }]}>
                  No thread owners assigned
                </Text>
              </View>
            ) : (
              <View style={styles.sectionBody}>
                {threadOwners.map((owner) => (
                  <ThreadOwnerRow key={owner.id} owner={owner} />
                ))}
              </View>
            )}
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

// Helper function to format dates
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${dateStr} ${timeStr}`;
};

// Helper function to capitalize first letter
const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Helper function to find highest role
const findHighestRole = (roles: any[]): string => {
  if (!roles || roles.length === 0) return 'Admin';

  const roleNamesInPriorityOrder = [
    'Resio Admin',
    'Organization Admin',
    'Billing Admin',
    'Property Admin',
  ];

  let highestRole = roles[0].name;

  roles.forEach((role) => {
    const currentIndex = roleNamesInPriorityOrder.indexOf(role.name);
    const highestIndex = roleNamesInPriorityOrder.indexOf(highestRole);

    if (currentIndex !== -1 && (highestIndex === -1 || currentIndex < highestIndex)) {
      highestRole = role.name;
    }
  });

  return highestRole;
};

// Participant Row Component
const ParticipantRow: React.FC<{
  participant: IConversationAccount;
  onPress?: (participant: IConversationAccount) => void;
}> = ({ participant, onPress }) => {
  const { redesign } = useAdminColors();
  const nameString = participant.assigneeAccount.firstName && participant.assigneeAccount.lastName
    ? `${participant.assigneeAccount.firstName} ${participant.assigneeAccount.lastName}`
    : participant.assigneeAccount.email;

  const hasLoggedIn = Boolean(participant.assigneeAccount.lastInteractionDate);
  const hasLeaseStatus = Boolean(
    participant.assigneeAccount.leaseCustomers?.length &&
    participant.assigneeAccount.leaseCustomers[0].lease?.statusType
  );

  const assigneeLC = participant.assigneeAccount.leaseCustomers?.[0];
  const leaseStatus = assigneeLC?.lease?.statusType;

  // Allow navigation if we have enough identifiers, regardless of login status
  const canOpenDetails = Boolean(
    participant.assigneeAccount?.id &&
    participant.assigneeAccount?.leaseCustomers?.[0]?.lease?.id
  );

  const handlePress = () => {
    if (canOpenDetails && onPress) {
      onPress(participant);
    }
  };

  return (
    <TouchableOpacity
      onPress={handlePress}
      disabled={!canOpenDetails}
      activeOpacity={0.7}
      style={styles.rowTouchable}
    >
      <GlassCard padding={12} style={styles.rowCard}>
        <View style={styles.rowContent}>
          <AvatarCircle
            firstName={participant.assigneeAccount.firstName || ''}
            lastName={participant.assigneeAccount.lastName || ''}
            size={40}
            style={styles.avatar}
          />

          <View style={styles.participantInfo}>
            <Text style={[styles.participantName, { color: redesign.text.primary }]}>
              {nameString}
            </Text>

            {hasLeaseStatus && (
              <Text style={[styles.participantStatus, { color: redesign.text.muted }]}>
                {capitalizeFirstLetter(leaseStatus || '')} resident
              </Text>
            )}

            {!hasLoggedIn && (
              <Text style={[styles.participantNoAccount, { color: redesign.text.subtle }]}>
                Has not created a Resio account
              </Text>
            )}
          </View>

          {canOpenDetails && (
            <SVG_COMPONENTS.CHEVRON_RIGHT
              width={16}
              height={16}
              color={redesign.text.muted}
            />
          )}
        </View>
      </GlassCard>
    </TouchableOpacity>
  );
};

// Thread Owner Row Component
const ThreadOwnerRow: React.FC<{
  owner: IConversationAccount;
}> = ({ owner }) => {
  const { redesign } = useAdminColors();
  const nameString = owner.assigneeAccount.firstName && owner.assigneeAccount.lastName
    ? `${owner.assigneeAccount.firstName} ${owner.assigneeAccount.lastName}`
    : owner.assigneeAccount.email;

  const assignedByNameString = owner.assignedBy?.firstName && owner.assignedBy?.lastName
    ? `${owner.assignedBy.firstName} ${owner.assignedBy.lastName}`
    : owner.assignedBy?.email || 'System';

  const highestRole = findHighestRole(owner.assigneeAccount.roles || []);
  const formattedDateTime = owner.assignedAt ? formatDateTime(owner.assignedAt) : '';

  return (
    <View style={styles.rowTouchable}>
      <GlassCard padding={16} style={styles.rowCard}>
        <View style={styles.threadOwnerHeader}>
          <AvatarCircle
            firstName={owner.assigneeAccount.firstName || ''}
            lastName={owner.assigneeAccount.lastName || ''}
            size={40}
            style={styles.avatar}
          />

          <View style={styles.threadOwnerInfo}>
            <Text style={[styles.threadOwnerName, { color: redesign.text.primary }]}>
              {nameString}
            </Text>
            <Text style={[styles.threadOwnerRole, { color: redesign.text.muted }]}>
              {highestRole}
            </Text>
          </View>
        </View>

        {owner.assignedAt && (
          <View style={styles.threadOwnerDetails}>
            <View style={styles.assignmentInfo}>
              <SVG_COMPONENTS.CALENDAR
                width={16}
                height={16}
                color={redesign.text.muted}
              />
              <Text style={[styles.assignmentDate, { color: redesign.text.primary }]}>
                {formattedDateTime}
              </Text>
            </View>
            <Text style={[styles.assignmentDescription, { color: redesign.text.subtle }]}>
              {nameString} assigned to this thread by {assignedByNameString}
            </Text>
          </View>
        )}
      </GlassCard>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    paddingTop: 20,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  content: {
    flex: 1,
    paddingTop: 20,
  },
  scrollContent: {
    paddingBottom: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  sectionBody: {
    paddingHorizontal: 16,
    gap: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 10,
  },
  sectionCount: {
    fontSize: 16,
  },
  conversationSubject: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
    lineHeight: 22,
  },
  conversationDate: {
    fontSize: 14,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
    paddingHorizontal: 16,
  },
  emptyStateText: {
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
  },
  rowTouchable: {
    marginBottom: 8,
  },
  rowCard: {
    // bg + border come from GlassCard
  },
  rowContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    marginRight: 12,
  },
  participantInfo: {
    flex: 1,
  },
  participantName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  participantStatus: {
    fontSize: 14,
  },
  participantNoAccount: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  threadOwnerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  threadOwnerInfo: {
    flex: 1,
  },
  threadOwnerName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  threadOwnerRole: {
    fontSize: 14,
  },
  threadOwnerDetails: {
    paddingLeft: 52,
  },
  assignmentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  assignmentDate: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 8,
  },
  assignmentDescription: {
    fontSize: 12,
    lineHeight: 16,
  },
});
